import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Plus, Trash2, Users2, RefreshCw, Save } from "lucide-react";
import { API } from "../../App";

/**
 * Profit Split Partners settings card.
 *
 * Lets the owner/admin configure the named partners that share company net
 * profit. Stored on the `business_settings` doc via /api/settings/profit-partners
 * and read by Financials → General tab + the PDF Profit Split section.
 *
 * Validation:
 *   • Each partner needs a name (max 80 chars).
 *   • Percentages must be > 0 and ≤ 100.
 *   • Sum of percentages must equal 100% (within 0.01 tolerance — the backend
 *     enforces this too; we surface a live warning in the UI for friction-less
 *     editing).
 */
const ProfitPartnersSection = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/settings/profit-partners`, { headers: headers() });
      setPartners(res.data?.partners || []);
      setDirty(false);
    } catch {
      toast.error("Failed to load profit partners");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const total = useMemo(
    () => partners.reduce((s, p) => s + (Number(p.percentage) || 0), 0),
    [partners],
  );

  // Sum tolerance must match the backend (0.01)
  const sumValid = Math.abs(total - 100) < 0.01 || partners.length === 0;

  const updatePartner = (idx, field, value) => {
    setPartners((prev) => prev.map((p, i) => (i === idx ? { ...p, [field]: value } : p)));
    setDirty(true);
  };

  const addPartner = () => {
    setPartners((prev) => {
      // Pre-fill with the remaining-to-100 percentage so the user's first add
      // is one click away from valid.
      const remaining = Math.max(0, 100 - total);
      return [...prev, { name: "", percentage: remaining > 0 ? Number(remaining.toFixed(2)) : 0 }];
    });
    setDirty(true);
  };

  const removePartner = (idx) => {
    setPartners((prev) => prev.filter((_, i) => i !== idx));
    setDirty(true);
  };

  const splitEvenly = () => {
    if (partners.length === 0) return;
    const each = +(100 / partners.length).toFixed(2);
    // Last partner absorbs the rounding remainder so sum = 100 exactly.
    const arr = partners.map(() => each);
    const adj = +(100 - each * (partners.length - 1)).toFixed(2);
    arr[arr.length - 1] = adj;
    setPartners((prev) => prev.map((p, i) => ({ ...p, percentage: arr[i] })));
    setDirty(true);
  };

  const save = async () => {
    if (!sumValid) {
      toast.error(`Percentages must sum to 100% (currently ${total.toFixed(2)}%)`);
      return;
    }
    if (partners.some((p) => !p.name?.trim())) {
      toast.error("Every partner needs a name");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        partners: partners.map((p) => ({
          name: p.name.trim(),
          percentage: Number(p.percentage) || 0,
        })),
      };
      const res = await axios.put(`${API}/settings/profit-partners`, payload, { headers: headers() });
      setPartners(res.data?.partners || []);
      setDirty(false);
      toast.success(payload.partners.length === 0 ? "Profit partners cleared" : "Profit partners saved");
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to save profit partners";
      toast.error(typeof msg === "string" ? msg : "Failed to save profit partners");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="glass-card border-theme" data-testid="profit-partners-card">
      <CardHeader className="p-4 sm:p-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-foreground font-['Manrope'] text-base sm:text-lg flex items-center gap-2">
              <Users2 className="w-4 h-4 text-[#FFCC00]" /> Profit Split Partners
            </CardTitle>
            <CardDescription className="text-muted-foreground text-xs sm:text-sm mt-1">
              Define how net profit is split. Used by Financials → General tab and PDF reports.
            </CardDescription>
          </div>
          {partners.length > 1 && (
            <Button variant="outline" size="sm" onClick={splitEvenly} data-testid="partners-split-evenly-btn">
              <RefreshCw className="w-4 h-4 mr-1.5" /> Split evenly
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="p-4 sm:p-6 pt-0">
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <>
            {partners.length === 0 ? (
              <div className="rounded-lg border border-dashed border-theme bg-theme-surface p-6 text-center" data-testid="partners-empty-state">
                <Users2 className="w-8 h-8 mx-auto text-muted-foreground/60 mb-2" />
                <p className="text-sm text-muted-foreground">No partners yet. Add one to start splitting net profit.</p>
                <p className="text-xs text-muted-foreground/70 mt-1">If left empty, the full net profit goes to the owner (no split).</p>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Column headers */}
                <div className="hidden sm:grid grid-cols-[1fr_140px_42px] gap-3 px-1 pb-1 text-xs uppercase tracking-wider text-muted-foreground">
                  <span>Partner name</span>
                  <span className="text-right">Percentage</span>
                  <span />
                </div>

                {partners.map((p, idx) => (
                  <div key={idx} className="grid grid-cols-1 sm:grid-cols-[1fr_140px_42px] gap-3 items-center" data-testid={`partner-row-${idx}`}>
                    <Input
                      value={p.name}
                      onChange={(e) => updatePartner(idx, "name", e.target.value)}
                      placeholder="e.g. Niv, Asaf, Shahar"
                      className="input-glass"
                      data-testid={`partner-name-${idx}`}
                    />
                    <div className="relative">
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={p.percentage}
                        onChange={(e) => updatePartner(idx, "percentage", e.target.value === "" ? "" : Number(e.target.value))}
                        className="input-glass pr-8 text-right"
                        data-testid={`partner-percentage-${idx}`}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">%</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removePartner(idx)}
                      data-testid={`partner-remove-${idx}`}
                      title="Remove partner"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}

                {/* Sum indicator */}
                <div className="flex items-center justify-between pt-2 border-t border-theme">
                  <Label className="text-sm text-muted-foreground">Total</Label>
                  <span
                    className={`text-base font-semibold ${sumValid ? "text-emerald-500" : "text-amber-500"}`}
                    data-testid="partners-percentage-total"
                  >
                    {total.toFixed(2)}%
                    {!sumValid && <span className="text-xs ml-2 font-normal">(must be exactly 100%)</span>}
                  </span>
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-2 mt-4">
              <Button variant="outline" onClick={addPartner} data-testid="partners-add-btn">
                <Plus className="w-4 h-4 mr-1.5" /> Add partner
              </Button>
              <div className="flex-1" />
              <Button
                onClick={save}
                disabled={saving || !dirty || !sumValid || partners.some((p) => !p.name?.trim())}
                data-testid="partners-save-btn"
                className="bg-gradient-to-r from-[#FFCC00] via-[#F5A623] to-[#FF9500] text-black font-semibold hover:opacity-90"
              >
                <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save"}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ProfitPartnersSection;
